import { getCsrfToken } from '@/integrations/php/client';

export type AdminDeleteBlockReason = 'self' | 'last_active' | null;

export interface AdminUserRecord {
  id: string;
  username: string;
  email: string;
  mobile: string;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
  isCurrent: boolean;
  canDelete: boolean;
  deleteBlockReason: AdminDeleteBlockReason;
}

export interface AdminUsersSummary {
  total: number;
  active: number;
  inactive: number;
  createdThisMonth: number;
  neverLoggedIn: number;
}

export interface AdminUsersDirectory {
  admins: AdminUserRecord[];
  summary: AdminUsersSummary;
  currentAdminId: string;
  generatedAt: string | null;
}

export interface CreateAdminUserInput {
  username: string;
  email: string;
  mobile: string;
  password: string;
}

export class AdminUsersApiError extends Error {
  readonly fields: Record<string, string>;
  readonly code: string | null;

  constructor(message: string, fields: Record<string, string> = {}, code: string | null = null) {
    super(message);
    this.name = 'AdminUsersApiError';
    this.fields = fields;
    this.code = code;
  }
}

const number = (value: unknown): number => Number(value || 0);
const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

function normalizeAdmin(value: unknown): AdminUserRecord {
  const admin = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const blockReason = admin.delete_block_reason;

  return {
    id: String(admin.id || ''),
    username: String(admin.username || ''),
    email: String(admin.email || ''),
    mobile: String(admin.mobile || ''),
    active: Boolean(admin.active),
    createdAt: nullableString(admin.created_at),
    updatedAt: nullableString(admin.updated_at),
    lastLoginAt: nullableString(admin.last_login_at),
    isCurrent: Boolean(admin.is_current),
    canDelete: Boolean(admin.can_delete),
    deleteBlockReason: blockReason === 'self' || blockReason === 'last_active' ? blockReason : null,
  };
}

function normalizeDirectory(value: unknown): AdminUsersDirectory {
  const directory = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const summary = (directory.summary && typeof directory.summary === 'object'
    ? directory.summary
    : {}) as Record<string, unknown>;

  return {
    admins: Array.isArray(directory.admins) ? directory.admins.map(normalizeAdmin) : [],
    summary: {
      total: number(summary.total),
      active: number(summary.active),
      inactive: number(summary.inactive),
      createdThisMonth: number(summary.created_this_month),
      neverLoggedIn: number(summary.never_logged_in),
    },
    currentAdminId: String(directory.current_admin_id || ''),
    generatedAt: nullableString(directory.generated_at),
  };
}

async function parseResponse(response: Response): Promise<AdminUsersDirectory> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new AdminUsersApiError(
      body?.error?.message || 'Δεν ήταν δυνατή η διαχείριση των χρηστών.',
      body?.error?.fields && typeof body.error.fields === 'object' ? body.error.fields : {},
      typeof body?.error?.code === 'string' ? body.error.code : null,
    );
  }

  return normalizeDirectory(body?.data);
}

export async function fetchAdminUsers(): Promise<AdminUsersDirectory> {
  const response = await fetch('/api/admin/admins.php', {
    credentials: 'include',
    headers: { 'X-CSRF-Token': await getCsrfToken() },
  });
  return parseResponse(response);
}

export async function createAdminUser(values: CreateAdminUserInput): Promise<AdminUsersDirectory> {
  const response = await fetch('/api/admin/admins.php', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await getCsrfToken(),
    },
    body: JSON.stringify(values),
  });
  return parseResponse(response);
}

export async function deleteAdminUser(adminId: string): Promise<AdminUsersDirectory> {
  const response = await fetch('/api/admin/admins.php', {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': await getCsrfToken(),
    },
    body: JSON.stringify({ id: adminId }),
  });
  return parseResponse(response);
}
